// ─── Centre d'assistance Junago — contenu statique (articles + FAQ) ───

export const ARTICLE_CATEGORIES = [
  { key: "copilot", label: "Junago Copilot" },
  { key: "demarrage", label: "Démarrage" },
  { key: "vente", label: "Site & ventes en ligne" },
  { key: "flotte", label: "Flotte" },
  { key: "reservations", label: "Réservations" },
  { key: "paiements", label: "Paiements" },
  { key: "legal", label: "Contrats & légal" },
  { key: "crm", label: "Clients & CRM" },
  { key: "equipe", label: "Équipe & sécurité" },
];

export const HELP_ARTICLES = [
  {
    slug: "junago-copilot-presentation",
    category: "copilot",
    title: "Junago Copilot : votre assistant IA au quotidien",
    excerpt:
      "Interrogez vos données en langage naturel, affichez planning, réservations, revenus en vignettes, et agissez sans naviguer dans les onglets.",
    readTime: "5 min",
    sections: [
      {
        heading: "Deux modes, un seul outil",
        paragraphs: [
          "Junago propose deux façons de travailler, basculables à tout moment via le bouton en haut à droite du dashboard : Junago, le logiciel complet avec ses onglets (Calendrier, Réservations, Clients, Véhicules, Devis, Revenus), et Junago Copilot, l'interface conversationnelle IA.",
          "Le principe : Junago pilote, Junago Copilot assiste. Les utilisateurs pressés passent par l'IA pour aller plus vite que la navigation par onglets.",
        ],
      },
      {
        heading: "Ce que vous pouvez demander",
        paragraphs: [
          "Le Copilot connaît l'intégralité de vos données : flotte (plaques, kilométrage, tarifs), réservations, clients, devis, agences et revenus. Toute question trouve une réponse chiffrée, accompagnée de vignettes interactives.",
        ],
        bullets: [
          "« Montre-moi mon planning du jour » — départs, retours et préparations en vignettes",
          "« Quelle est ma réservation de ce matin ? » — la fiche s'affiche dans la conversation",
          "« Quel est mon véhicule le plus rentable ? » — réponse chiffrée immédiate",
          "« Combien ai-je encaissé ce mois-ci ? » — synthèse financière en un message",
        ],
      },
      {
        heading: "Des vignettes qui agissent",
        paragraphs: [
          "Chaque vignette est cliquable : ouvrez une réservation dans une fenêtre sombre sans quitter la conversation, acceptez ou refusez une demande en attente, ou cliquez sur « Modifier » pour ouvrir la fiche complète dans Junago. Votre conversation vous attend, intacte, à votre retour.",
        ],
      },
    ],
  },
  {
    slug: "copilot-taches-recommandees",
    category: "copilot",
    title: "Les tâches recommandées par l'IA",
    excerpt:
      "Contrats à signer, états des lieux, entretiens, relances de devis : le Copilot génère vos tâches automatiquement et vous les cochez d'un clic.",
    readTime: "4 min",
    sections: [
      {
        heading: "Des tâches générées automatiquement",
        paragraphs: [
          "Junago analyse en permanence votre activité et génère les tâches nécessaires : faire signer un contrat, réaliser un état des lieux, planifier un entretien ou un contrôle technique, relancer un devis envoyé, mettre à jour un document.",
          "Le bouton ⚡ en haut à droite du Pilotage — visible dans les modes Junago et Copilot — affiche le nombre de tâches en attente. Un clic les fait apparaître en vignettes dans la conversation du Copilot.",
        ],
      },
      {
        heading: "Marquer comme terminée",
        paragraphs: [
          "Chaque vignette de tâche propose deux actions : le bouton d'action direct (« Ouvrir le contrat », « Faire l'état des lieux »…) et « Marquer comme terminée » lorsque l'intervention est faite (entretien réalisé, devis relancé, permis mis à jour…).",
          "Beaucoup de tâches se marquent aussi toutes seules : signez le contrat, et la tâche disparaît automatiquement.",
        ],
      },
      {
        heading: "Créer une réservation dans le chat",
        paragraphs: [
          "Le bouton « + Réservation » au-dessus de la zone de saisie ouvre une fenêtre de création directement dans la conversation : véhicule, client, dates, statut — la réservation apparaît instantanément sur votre calendrier.",
        ],
      },
    ],
  },
  {
    slug: "prise-en-main-pilotage",
    category: "demarrage",
    title: "Prendre en main le Pilotage Junago",
    excerpt:
      "Le Pilotage est votre tour de contrôle : calendrier central, réservations, tâches automatiques et revenus au même endroit.",
    readTime: "8 min",
    sections: [
      {
        heading: "Une tour de contrôle unique",
        paragraphs: [
          "Le Pilotage Junago centralise toute votre activité de location dans une seule interface, organisée autour d'un calendrier central façon Google Calendar. Chaque réservation, chaque tâche et chaque véhicule y est visible d'un coup d'œil.",
          "Depuis la barre latérale, deux entrées suffisent : Pilotage pour l'opérationnel quotidien, Paramétrages pour la configuration de votre compte.",
        ],
      },
      {
        heading: "Les 12 onglets du Pilotage",
        paragraphs: [
          "Le Pilotage se décompose en onglets spécialisés : Calendrier, Réservations, Devis, Véhicules, Clients, Prospects, Revenus et plus encore. Vous naviguez entre eux sans jamais quitter la page. Les tâches, elles, vivent dans le Copilot (bouton ⚡).",
        ],
        bullets: [
          "Calendrier — vue Gantt de votre flotte, jour par jour",
          "Réservations — cycle de vie complet, du devis à la restitution",
          "Véhicules — gestion de flotte détaillée avec documents et alertes",
          "Revenus — suivi de votre chiffre d'affaires en temps réel",
        ],
      },
      {
        heading: "Le bouton Créer",
        paragraphs: [
          "Le bouton violet « + » en haut à droite vous permet de créer instantanément une réservation, un client, un prospect, un véhicule ou un devis, depuis n'importe quel écran du back office. Les tâches, elles, sont générées automatiquement par Junago Pilot.",
        ],
      },
      {
        heading: "Les tâches automatiques, votre copilote",
        paragraphs: [
          "À chaque réservation confirmée, Junago génère automatiquement les actions à réaliser : faire signer le contrat, préparer le véhicule, effectuer l'état des lieux de départ, planifier la restitution. Ces tâches apparaissent dans le calendrier et dans l'onglet Tâches, avec un badge indiquant le nombre d'actions en attente.",
          "Vous pouvez également créer vos propres tâches (révision, nettoyage, rendez-vous garage) et les rattacher à un véhicule ou une réservation. Terminée ? Cochez-la, elle est archivée avec son historique.",
        ],
      },
      {
        heading: "Bien démarrer : la checklist des premiers jours",
        paragraphs: [
          "Pour tirer le meilleur de Junago dès la première semaine, suivez ces étapes dans l'ordre :",
        ],
        bullets: [
          "1. Renseignez les informations de votre entreprise dans Paramétrages (elles alimentent vos contrats et devis)",
          "2. Créez vos catégories de véhicules et vos statuts personnalisés",
          "3. Ajoutez vos véhicules avec photos, documents et dates d'expiration",
          "4. Activez les encaissements Stripe si vous souhaitez encaisser par carte",
          "5. Créez votre première réservation via le bouton « + » et suivez les tâches générées",
        ],
      },
      {
        heading: "Astuce de pro",
        paragraphs: [
          "Commencez chaque journée par l'onglet Tâches : c'est votre to-do list opérationnelle. Un départ prévu aujourd'hui ? La tâche « État des lieux de départ » vous y attend, avec un accès direct à la réservation concernée.",
        ],
      },
    ],
  },
  {
    slug: "votre-page-de-reservation-en-ligne",
    category: "vente",
    title: "Votre site internet de réservation en ligne",
    excerpt:
      "Activez un site internet professionnel généré et hébergé par Junago : design premium, SEO, disponibilités en temps réel, paiement en ligne — tout synchronisé avec votre logiciel.",
    readTime: "6 min",
    sections: [
      {
        heading: "Un site internet généré depuis vos données",
        paragraphs: [
          "Depuis le menu « Vendre », Junago génère et héberge votre site internet de réservation : votre logo, vos véhicules actifs avec leur page dédiée, vos tarifs (grille saisonnière appliquée automatiquement), vos agences avec carte, vos conditions et votre assurance. Hébergement et certificat SSL sont inclus, avec une adresse Junago par défaut.",
          "Tout est synchronisé en temps réel avec votre logiciel : modifiez un tarif, une option ou ajoutez un véhicule, le site se met à jour tout seul — aucune double saisie. Si une information n'est pas renseignée, la section correspondante ne s'affiche simplement pas.",
        ],
      },
      {
        heading: "La configuration guidée",
        paragraphs: [
          "Un assistant en 5 étapes vous accompagne : choix du template (Premium, Startup, Fun ou Professionnel), vérification du logo (repris de Paramétrages → Entreprise), mots clés SEO avec les conseils Junago, FAQ générée par l'IA à partir de vos conditions et documents juridiques, puis publication.",
          "Conseil SEO : mettez en avant votre activité principale et votre ville — par exemple « location de voiture Guadeloupe ». C'est ce que vos clients tapent sur Google.",
        ],
        bullets: [
          "Template — 4 designs premium, modifiables à tout moment",
          "Contenu & SEO — description générée par l'IA à partir de vos mots clés",
          "Contact & FAQ — WhatsApp, réseaux sociaux, questions fréquentes",
          "Publication & partage — lien à diffuser sur WhatsApp, Facebook, Instagram",
        ],
      },
      {
        heading: "Réservation et paiement en ligne",
        paragraphs: [
          "Vos clients choisissent leurs dates, leurs options (livraison, rachat de franchise…) et paient par carte via Stripe (chiffrement SSL, 3D Secure). La réservation confirmée apparaît instantanément dans votre Pilotage.",
          "Le badge « Entreprise vérifiée » rassure vos visiteurs : il atteste que votre identité, votre KBIS et votre assurance ont été contrôlés, et que les disponibilités affichées sont synchronisées depuis votre logiciel de gestion.",
        ],
      },
    ],
  },
  {
    slug: "gerer-votre-flotte",
    category: "flotte",
    title: "Gérer votre flotte de véhicules",
    excerpt:
      "Créez des fiches véhicules complètes : VIN, kilométrage, Crit'Air, équipements, documents avec alertes d'expiration.",
    readTime: "9 min",
    sections: [
      {
        heading: "Des fiches véhicules complètes",
        paragraphs: [
          "Chaque véhicule dispose d'une fiche détaillée : immatriculation, VIN, kilométrage, vignette Crit'Air, carburant, transmission, équipements et photos. Plus votre fiche est complète, plus votre suivi de flotte est précis.",
        ],
      },
      {
        heading: "Documents et alertes d'expiration",
        paragraphs: [
          "Stockez les documents de chaque véhicule (carte grise, assurance, contrôle technique) directement sur la fiche. Ajoutez une date d'expiration et Junago vous alerte automatiquement avant l'échéance.",
        ],
        bullets: [
          "Carte grise et certificat d'immatriculation",
          "Attestation d'assurance flotte",
          "Contrôle technique avec date de validité",
          "Factures d'entretien et de réparation",
        ],
      },
      {
        heading: "Catégories et statuts personnalisés",
        paragraphs: [
          "Dans Paramétrages > Flotte, créez vos propres catégories (citadine, SUV, utilitaire, prestige…) et vos statuts personnalisés avec couleurs (disponible, en entretien, immobilisé…). Réorganisez-les par glisser-déposer.",
        ],
      },
      {
        heading: "Historique et traçabilité",
        paragraphs: [
          "Chaque action sur un véhicule est historisée : création, modification, ajout de document, changement de coûts. Vous disposez d'une timeline complète pour chaque véhicule de votre parc.",
        ],
      },
      {
        heading: "Suivre les coûts et la rentabilité",
        paragraphs: [
          "Renseignez le prix d'achat, l'assurance annuelle et l'entretien estimé de chaque véhicule : Junago calcule automatiquement la rentabilité par véhicule sur la période observée — revenu brut, revenu net, jours loués et marge estimée.",
          "Combiné aux charges mensuelles définies dans vos paramétrages financiers (assurance flotte, loyer d'agence, comptable), vous savez précisément quels véhicules rapportent et lesquels coûtent.",
        ],
      },
      {
        heading: "Les vues et filtres qui font gagner du temps",
        paragraphs: [
          "La liste des véhicules se consulte en vue cartes (visuelle, avec photos) ou en vue tableau (dense, triable). Les filtres avancés combinent catégorie, statut, carburant, transmission et ville pour retrouver un véhicule en un instant, même dans une grande flotte.",
        ],
      },
      {
        heading: "Bonnes pratiques",
        bullets: [
          "Photographiez chaque véhicule sous plusieurs angles dès son ajout",
          "Ajoutez systématiquement une date d'expiration sur l'assurance et le contrôle technique",
          "Utilisez un statut « En entretien » coloré pour repérer les immobilisations d'un coup d'œil",
          "Mettez à jour le kilométrage après chaque location pour anticiper les révisions",
        ],
      },
    ],
  },
  {
    slug: "cycle-de-vie-reservation",
    category: "reservations",
    title: "Réservations : du devis à la restitution",
    excerpt:
      "Suivez chaque location étape par étape : devis, confirmation, contrat, état des lieux départ et retour.",
    readTime: "10 min",
    sections: [
      {
        heading: "Le cycle de vie d'une location",
        paragraphs: [
          "Junago structure chaque location en étapes claires. À chaque étape, les tâches nécessaires sont générées automatiquement pour que rien ne soit oublié.",
        ],
        bullets: [
          "1. Devis — envoyez une proposition chiffrée à votre client",
          "2. Réservation confirmée — encaissement sécurisé via Stripe",
          "3. Contrat — génération et signature électronique",
          "4. État des lieux de départ — photos et relevés horodatés",
          "5. État des lieux de retour — comparaison avec le départ",
          "6. Clôture — la réservation est archivée, les revenus comptabilisés",
        ],
      },
      {
        heading: "Tâches automatiques",
        paragraphs: [
          "Dès qu'une réservation est confirmée, Junago crée automatiquement les tâches associées : faire signer le contrat, réaliser l'état des lieux de départ, préparer le véhicule, planifier la restitution. Elles apparaissent dans votre calendrier et votre onglet Tâches.",
        ],
      },
      {
        heading: "Réservations manuelles",
        paragraphs: [
          "Un client vous appelle ou passe à l'agence ? Créez une réservation en quelques secondes via le bouton « + ». Elle bénéficie du suivi complet : contrat, état des lieux, tâches automatiques.",
        ],
      },
      {
        heading: "Gérer les devis et les relances",
        paragraphs: [
          "Avant la réservation, il y a souvent un devis. Créez-le depuis l'onglet Devis : véhicule, dates, options, remise éventuelle. Envoyez-le à votre prospect et suivez son statut (envoyé, accepté, refusé, expiré).",
          "Un devis sans réponse ? Planifiez une tâche de relance directement depuis la fiche prospect. Un devis accepté se transforme en réservation confirmée en un clic, sans ressaisie.",
        ],
      },
      {
        heading: "Que faire en cas de litige au retour ?",
        paragraphs: [
          "C'est là que les états des lieux prennent toute leur valeur : les photos horodatées du départ et du retour sont comparées côte à côte. Une rayure absente au départ et présente au retour ? Vous disposez d'une preuve datée, attachée au contrat signé.",
        ],
      },
      {
        heading: "Bon à savoir",
        bullets: [
          "Le calendrier détecte automatiquement les conflits de dates sur un même véhicule",
          "Vous pouvez modifier les dates d'une réservation tant qu'elle n'est pas clôturée",
          "Les réservations clôturées alimentent automatiquement vos statistiques de revenus",
        ],
      },
    ],
  },
  {
    slug: "contrats-signature-electronique",
    category: "legal",
    title: "Contrats et signature électronique",
    excerpt:
      "Générez des contrats de location conformes et faites-les signer électroniquement, sur place ou à distance.",
    readTime: "7 min",
    sections: [
      {
        heading: "Génération automatique du contrat",
        paragraphs: [
          "Pour chaque réservation confirmée, Junago génère un contrat de location pré-rempli : identité du locataire, véhicule, dates, tarif, conditions. Fini les documents Word recopiés à la main.",
        ],
      },
      {
        heading: "Signature électronique",
        paragraphs: [
          "Le locataire signe directement sur l'écran — smartphone, tablette ou ordinateur. La signature est horodatée et attachée au contrat. Vous et votre client recevez chacun un exemplaire.",
        ],
      },
      {
        heading: "États des lieux photographiés",
        paragraphs: [
          "Au départ comme au retour, réalisez un état des lieux photo directement depuis Junago : carrosserie, kilométrage, niveau de carburant. Les deux états des lieux sont comparés automatiquement en cas de litige.",
        ],
      },
      {
        heading: "Personnaliser vos contrats",
        paragraphs: [
          "Les informations de votre entreprise (raison sociale, SIRET, adresse, conditions) renseignées dans les Paramétrages alimentent automatiquement chaque contrat. Vos documents sont ainsi cohérents et professionnels, sans effort.",
        ],
      },
      {
        heading: "Signer à distance, pas à pas",
        paragraphs: [
          "Votre client n'est pas sur place ? La signature à distance suit un parcours simple :",
        ],
        bullets: [
          "1. Le contrat est généré automatiquement à la confirmation de la réservation",
          "2. Le client reçoit un lien sécurisé vers son espace",
          "3. Il relit le contrat et signe directement sur son écran",
          "4. La signature est horodatée et le contrat archivé pour les deux parties",
        ],
      },
      {
        heading: "Archivage et valeur de preuve",
        paragraphs: [
          "Chaque contrat signé est conservé avec sa signature horodatée et rattaché à la réservation, au client et au véhicule. En cas de litige, vous retrouvez en quelques secondes le contrat, les états des lieux photo et l'historique des échanges — un dossier complet, daté et opposable.",
        ],
      },
    ],
  },
  {
    slug: "paiements-securises-stripe",
    category: "paiements",
    title: "Encaissements sécurisés avec Stripe",
    excerpt:
      "Encaissez vos locations par carte bancaire grâce à Stripe, directement depuis votre back office.",
    readTime: "7 min",
    sections: [
      {
        heading: "Comment fonctionnent les encaissements ?",
        paragraphs: [
          "Junago s'appuie sur Stripe, leader mondial du paiement en ligne, pour traiter vos encaissements. Votre client paie par carte bancaire ; les fonds sont ensuite reversés sur votre compte bancaire.",
          "Aucune donnée bancaire n'est stockée sur les serveurs Junago : tout est chiffré et géré par Stripe, certifié PCI-DSS niveau 1.",
        ],
      },
      {
        heading: "Activer les encaissements",
        paragraphs: [
          "Un parcours d'onboarding Stripe vous guide : identité du dirigeant, informations de l'entreprise, IBAN. Une fois validé, vous pouvez encaisser vos clients par carte bancaire.",
        ],
      },
      {
        heading: "Suivi des revenus",
        paragraphs: [
          "L'onglet Revenus du Pilotage vous donne une vision claire de votre chiffre d'affaires : encaissements par période, par véhicule, et évolution mensuelle.",
        ],
      },
      {
        heading: "Ce que Stripe garantit",
        paragraphs: [
          "En confiant vos encaissements à Stripe, vous bénéficiez du même niveau de sécurité que les plus grandes plateformes mondiales :",
        ],
        bullets: [
          "Certification PCI-DSS niveau 1, le plus haut standard de l'industrie",
          "3D-Secure inclus sur chaque transaction",
          "Toutes cartes acceptées, transactions en euros",
          "Virements automatiques sur votre IBAN sous 48h",
        ],
      },
      {
        heading: "Suivre vos virements",
        paragraphs: [
          "Chaque encaissement apparaît dans l'onglet Revenus avec son statut. Vous suivez le solde disponible, les virements programmés et l'historique complet des transactions, réconciliés automatiquement avec vos réservations.",
        ],
      },
      {
        heading: "Bon à savoir",
        paragraphs: [
          "L'encaissement en ligne est optionnel : vous pouvez continuer d'encaisser par virement, chèque ou espèces et simplement enregistrer la réservation dans Junago. Les statistiques de revenus restent alimentées dans tous les cas.",
        ],
      },
    ],
  },
  {
    slug: "crm-clients-prospects",
    category: "crm",
    title: "CRM intégré : clients et prospects",
    excerpt:
      "Centralisez vos contacts, suivez vos prospects et retrouvez l'historique complet de chaque client.",
    readTime: "8 min",
    sections: [
      {
        heading: "Une fiche par client",
        paragraphs: [
          "Chaque locataire dispose d'une fiche client : coordonnées, permis de conduire, historique des locations, contrats signés. Lorsqu'un client réserve à nouveau, tout est déjà pré-rempli.",
        ],
      },
      {
        heading: "Suivi des prospects",
        paragraphs: [
          "Un contact intéressé mais pas encore décidé ? Créez un prospect, associez-lui un devis et suivez la relance depuis votre calendrier. Quand il confirme, le prospect devient client en un clic.",
        ],
      },
      {
        heading: "Devis professionnels",
        paragraphs: [
          "Générez des devis chiffrés (véhicule, dates, options, remises) et envoyez-les à vos prospects. Un devis accepté se transforme directement en réservation.",
        ],
      },
      {
        heading: "Du prospect au client fidèle : le parcours type",
        paragraphs: [
          "Voici comment les loueurs Junago structurent leur suivi commercial :",
        ],
        bullets: [
          "1. Un contact appelle ou écrit → créez un prospect en 10 secondes",
          "2. Envoyez-lui un devis chiffré depuis l'onglet Devis",
          "3. Pas de réponse sous 3 jours ? Une tâche de relance vous le rappelle",
          "4. Devis accepté → le prospect devient client, la réservation est créée",
          "5. Après la location, son historique enrichit sa fiche pour les prochaines fois",
        ],
      },
      {
        heading: "Exploiter l'historique client",
        paragraphs: [
          "La fiche client concentre tout : locations passées, contrats signés, chiffre d'affaires généré. Un client régulier qui rappelle ? Vous connaissez ses préférences, son permis est déjà enregistré, et la nouvelle réservation se crée deux fois plus vite.",
          "C'est aussi un outil commercial : identifiez vos meilleurs clients par chiffre d'affaires et soignez-les — ce sont eux qui recommandent votre agence.",
        ],
      },
    ],
  },
  {
    slug: "parametrages-personnalisation",
    category: "demarrage",
    title: "Paramétrages : personnaliser Junago",
    excerpt:
      "Adaptez la plateforme à votre façon de travailler : catégories, statuts, champs personnalisés et alertes.",
    readTime: "7 min",
    sections: [
      {
        heading: "Tout est configurable",
        paragraphs: [
          "L'espace Paramétrages vous permet d'adapter Junago à votre activité : catégories de véhicules, statuts avec couleurs, champs personnalisés sur les réservations, alertes de documents.",
        ],
        bullets: [
          "Catégories de flotte réorganisables par glisser-déposer",
          "Statuts personnalisés avec code couleur",
          "Champs personnalisés sur les réservations",
          "Seuils d'alerte pour l'expiration des documents",
        ],
      },
      {
        heading: "Informations de l'entreprise",
        paragraphs: [
          "Renseignez les informations de votre société (raison sociale, SIRET, adresse) : elles alimentent automatiquement vos contrats et devis.",
        ],
      },
      {
        heading: "Les champs personnalisés en pratique",
        paragraphs: [
          "Besoin de suivre une information spécifique sur vos réservations — numéro de vol, lieu de livraison, siège bébé ? Créez un champ personnalisé dans Paramétrages > Réservations : il apparaîtra sur chaque réservation et pourra être rempli par votre équipe.",
        ],
      },
      {
        heading: "Alertes : ne ratez plus une échéance",
        paragraphs: [
          "Définissez le délai d'alerte (15, 30, 60 jours) avant l'expiration des documents de vos véhicules. Junago surveille les dates du contrôle technique, de l'assurance et de tout document daté, et vous prévient à temps pour agir.",
        ],
      },
      {
        heading: "Conseils de configuration",
        bullets: [
          "Limitez-vous à 4-6 catégories de véhicules : trop de catégories nuit à la lisibilité",
          "Attribuez une couleur distincte à chaque statut pour un repérage instantané dans le calendrier",
          "Configurez la TVA et vos charges fixes dès le départ : vos calculs de rentabilité seront justes dès le premier mois",
          "Revoyez vos paramétrages chaque trimestre à mesure que votre activité évolue",
        ],
      },
    ],
  },
  {
    slug: "junago-copilot-assistant-ia",
    category: "demarrage",
    title: "Junago Copilot : votre assistant IA",
    excerpt:
      "Rapports d'activité, véhicules stars, fréquences de réservation : Copilot analyse vos données et vous aide à décider.",
    readTime: "6 min",
    sections: [
      {
        heading: "Un assistant, pas un agent",
        paragraphs: [
          "Junago Copilot ne prend aucune décision à votre place et ne modifie rien dans votre compte. Son rôle : lire vos données d'activité (réservations, véhicules, revenus) et vous les expliquer clairement, en français, pour éclairer vos choix.",
        ],
      },
      {
        heading: "Ce que Copilot analyse",
        bullets: [
          "Vos véhicules les plus loués et les plus rentables",
          "Les fréquences de réservation : jours populaires, saisonnalité, durées moyennes",
          "L'évolution de vos revenus mois par mois",
          "Les signaux faibles : véhicule qui dort, créneau sous-exploité",
        ],
      },
      {
        heading: "Générer un rapport",
        paragraphs: [
          "Rendez-vous dans Pilotage > Copilot et cliquez sur « Générer mon rapport ». En quelques secondes, Copilot produit un résumé de votre activité, vos véhicules stars, les tendances observées et 3 recommandations concrètes.",
          "Chaque rapport est conservé : vous pouvez le regénérer à tout moment pour suivre l'évolution de votre activité.",
        ],
      },
      {
        heading: "Bon à savoir",
        paragraphs: [
          "Copilot a besoin d'un minimum de données : ajoutez vos premières réservations avant de générer votre premier rapport. Plus votre historique est riche, plus l'analyse est pertinente.",
        ],
      },
    ],
  },
  {
    slug: "produits-options-extras",
    category: "vente",
    title: "Vendre des produits & options (extras)",
    excerpt:
      "Sièges bébé, rachat de franchise, livraison : créez vos options, gérez le stock par agence et proposez-les automatiquement sur votre site de réservation et vos devis.",
    readTime: "5 min",
    sections: [
      {
        heading: "Créer vos options et produits",
        paragraphs: [
          "Rendez-vous dans Paramétrages → Produits & options. Chaque option a un libellé, un prix (par jour ou par réservation), une TVA et une description. Elle apparaît automatiquement à l'étape des extras sur votre site de réservation en ligne et dans le calcul de vos devis et réservations manuelles.",
          "Vous pouvez réordonner vos options par glisser-déposer pour mettre en avant les plus rentables (rachat de franchise, livraison…).",
        ],
      },
      {
        heading: "Stock strict, souple ou illimité",
        paragraphs: [
          "En stock strict, Junago bloque la vente dès que la quantité disponible est atteinte sur la période (idéal pour 5 sièges bébé). En stock souple, la vente reste possible mais vous êtes alerté. Les services sans limite physique (assurances, nettoyage) restent en illimité.",
          "Si vous avez plusieurs agences, définissez un stock par agence : chaque site vend ce qu'il a réellement sur place, avec repli automatique sur le stock global si vous ne précisez rien.",
        ],
      },
      {
        heading: "Suivre la rentabilité",
        paragraphs: [
          "L'onglet Revenus consolide le chiffre d'affaires, le volume et la marge de chaque produit : vous savez immédiatement quelles options performent et lesquelles ajuster.",
        ],
      },
    ],
  },
  {
    slug: "revenus-facturation-export",
    category: "paiements",
    title: "Revenus, factures, avoirs et export comptable",
    excerpt:
      "Le cockpit financier de Junago : synthèse des revenus, encaissements, cautions, factures conformes, avoirs, dépenses et export CSV mensuel pour votre comptable.",
    readTime: "7 min",
    sections: [
      {
        heading: "Le cockpit Revenus",
        paragraphs: [
          "L'onglet Revenus regroupe six vues : Synthèse (KPIs et graphiques), Encaissements (par canal), Cautions, Factures, Dépenses et Rentabilité par véhicule. Chaque vue se filtre par période : jour, semaine, mois, année ou dates personnalisées.",
        ],
      },
      {
        heading: "Factures et avoirs conformes",
        paragraphs: [
          "Générez une facture depuis n'importe quelle réservation, ou créez une facture libre. La numérotation est continue et conforme, la TVA est calculée automatiquement. En cas d'erreur ou de remboursement, créez un avoir en un clic : il est lié à la facture d'origine.",
        ],
      },
      {
        heading: "Cautions maîtrisées",
        paragraphs: [
          "Chaque caution est tracée avec sa réservation : empreinte bancaire au départ, capture partielle ou totale en cas de dommage constaté à l'état des lieux, libération en fin de location. Vous savez toujours ce qui est bloqué, capturé ou libéré.",
        ],
      },
      {
        heading: "Export comptable mensuel",
        paragraphs: [
          "Depuis la Synthèse, exportez un CSV propre par période contenant toutes vos écritures (encaissements, factures, avoirs, dépenses) : votre expert-comptable l'importe directement dans son outil.",
        ],
      },
    ],
  },
  {
    slug: "equipe-roles-permissions",
    category: "equipe",
    title: "Équipe : rôles, permissions et sécurité",
    excerpt:
      "Créez des rôles sur mesure, limitez un collaborateur à une agence, suivez chaque action dans le journal d'activité et protégez votre compte.",
    readTime: "6 min",
    sections: [
      {
        heading: "Inviter des collaborateurs et créer des rôles",
        paragraphs: [
          "Dans Paramétrages → Équipe & sécurité, invitez vos collaborateurs et attribuez-leur un rôle. Chaque rôle définit finement les droits par module (réservations, véhicules, clients, devis, finances, paramétrages…) et par action : lecture, création, modification, suppression, export.",
          "Un agent de comptoir peut ainsi créer des réservations sans jamais accéder à vos revenus ; un comptable voit les finances sans pouvoir modifier la flotte.",
        ],
      },
      {
        heading: "Restriction par agence",
        paragraphs: [
          "Pour les loueurs multi-sites : rattachez un collaborateur à une agence précise. Il ne verra que les réservations dont le départ a lieu dans son agence — dans l'interface comme dans les exports.",
        ],
      },
      {
        heading: "Journal d'activité et protection du compte",
        paragraphs: [
          "Chaque action sensible est enregistrée dans le journal d'activité avec son auteur, la date et l'adresse IP : modification de tarif, annulation, changement de permissions… Vous savez toujours qui a fait quoi.",
          "La connexion est verrouillée automatiquement pendant 15 minutes après 5 tentatives échouées, pour bloquer les attaques par force brute.",
        ],
      },
    ],
  },
  {
    slug: "emails-automatiques-modeles",
    category: "crm",
    title: "Emails automatiques et modèles personnalisés",
    excerpt:
      "Confirmation, rappel avant départ, envoi du contrat ou de la facture : configurez les emails que Junago envoie à votre place, avec vos couleurs et vos textes.",
    readTime: "5 min",
    sections: [
      {
        heading: "Des emails envoyés au bon moment",
        paragraphs: [
          "Junago déclenche automatiquement les bons emails selon le statut de la réservation : confirmation à la création, rappel avant le départ, contrat prêt à signer, lien d'acceptation du devis, facture après paiement… Vous n'avez rien à taper, le client est toujours informé.",
        ],
      },
      {
        heading: "Personnaliser vos modèles",
        paragraphs: [
          "Dans Paramétrages → Emails, chaque modèle est modifiable : objet, texte, couleurs, signature. Utilisez les variables dynamiques ({{client}}, {{vehicule}}, {{dates}}, {{montant}}…) pour des messages toujours justes, et prévisualisez le rendu avant d'activer.",
          "Un email de test peut être envoyé à votre propre adresse pour valider le rendu final.",
        ],
      },
      {
        heading: "Historique des envois",
        paragraphs: [
          "Chaque email envoyé est tracé et consultable depuis la fiche de la réservation : vous savez exactement ce que le client a reçu et quand.",
        ],
      },
    ],
  },
  {
    slug: "publier-site-reservation",
    category: "vente",
    title: "Publier votre site internet de réservation en ligne",
    excerpt:
      "De la personnalisation à la mise en ligne : configurez votre site, prévisualisez-le en privé, publiez-le et collectez vos premiers avis clients.",
    readTime: "6 min",
    sections: [
      {
        heading: "Personnaliser votre site avant publication",
        paragraphs: [
          "Dans Paramétrages → Site de réservation, renseignez l'identité de votre agence : nom, logo, couleurs, textes d'accueil, coordonnées et horaires. Choisissez les véhicules à publier — vous contrôlez, véhicule par véhicule, ce qui est visible en ligne avec ses tarifs, durées minimum et conditions.",
          "Vos produits & options (sièges bébé, rachat de franchise, livraison…) apparaissent automatiquement à l'étape des extras du parcours de réservation.",
        ],
      },
      {
        heading: "Prévisualiser en privé, publier en un clic",
        paragraphs: [
          "Avant la mise en ligne, générez un lien de prévisualisation privé : vous voyez le site exactement comme vos clients, sans qu'il soit accessible publiquement. Quand tout est prêt, cliquez sur « Publier » : votre site est immédiatement en ligne sur son adresse dédiée, hébergement et SSL inclus.",
          "Vous pouvez dépublier à tout moment : le site repasse en mode privé sans perdre sa configuration.",
        ],
      },
      {
        heading: "Avis clients et référencement",
        paragraphs: [
          "Vos clients peuvent déposer un avis sur votre site : les avis validés renforcent la confiance des visiteurs suivants. Le site est optimisé pour le référencement (titres, descriptions, structure) — partagez son adresse sur votre fiche Google Business, Instagram et WhatsApp pour développer vos réservations directes.",
        ],
      },
      {
        heading: "Tout reste synchronisé",
        paragraphs: [
          "Chaque modification dans votre logiciel — tarif, disponibilité, nouveau véhicule, option — est répercutée instantanément sur le site. Il n'y a rien à maintenir : le site affiche toujours la réalité de votre planning.",
        ],
      },
    ],
  },
  {
    slug: "stock-options-par-agence",
    category: "vente",
    title: "Gérer le stock d'options par agence",
    excerpt:
      "Stock strict, souple ou illimité, quantités par agence et repli automatique : vendez exactement ce que chaque site a réellement en rayon.",
    readTime: "4 min",
    sections: [
      {
        heading: "Trois modes de stock",
        paragraphs: [
          "Chaque produit ou option se configure selon sa nature. En stock strict, la vente est bloquée dès que la quantité disponible est atteinte sur la période : idéal pour les objets physiques comme les sièges bébé ou les GPS. En stock souple, la vente reste possible au-delà du seuil mais vous êtes alerté. En illimité, aucun plafond : parfait pour les services (assurance, nettoyage, conducteur additionnel).",
        ],
      },
      {
        heading: "Des quantités propres à chaque agence",
        paragraphs: [
          "Si vous avez plusieurs agences, définissez une quantité par site : 5 sièges bébé à Bordeaux, 3 à Toulouse. Chaque agence ne vend que ce qu'elle possède réellement, en ligne comme au comptoir.",
          "Si aucune quantité n'est précisée pour une agence, Junago se replie automatiquement sur le stock global du produit : vous n'avez rien à dupliquer.",
        ],
      },
      {
        heading: "Le stock suit les réservations",
        paragraphs: [
          "La disponibilité est calculée sur la période de location : un siège bébé loué du 10 au 15 redevient disponible le 16. Annulation ? Le stock est immédiatement libéré. Vous suivez le volume et le chiffre d'affaires de chaque option dans l'onglet Revenus.",
        ],
      },
    ],
  },
  {
    slug: "cautions-empreinte-capture-liberation",
    category: "paiements",
    title: "Cautions : empreinte bancaire, capture et libération",
    excerpt:
      "Bloquez la caution sans débiter, capturez le montant exact d'un dommage avec preuves, libérez automatiquement au retour : le guide complet.",
    readTime: "6 min",
    sections: [
      {
        heading: "L'empreinte bancaire remplace le chèque",
        paragraphs: [
          "Au départ du véhicule, Junago pose une empreinte bancaire sur la carte du client : le montant de la caution est bloqué mais jamais débité. Pas de chèque à conserver, pas de refus des clients étrangers, pas d'espèces à gérer.",
          "Le montant de la caution se définit par véhicule ou par catégorie dans vos paramétrages, et apparaît clairement dans le contrat et le parcours de réservation.",
        ],
      },
      {
        heading: "Capturer en cas de dommage",
        paragraphs: [
          "Au retour, l'état des lieux comparé révèle un dommage ? Capturez exactement le montant de la réparation — en partie ou en totalité — directement depuis la réservation. Les photos horodatées de l'état des lieux servent de preuve en cas de contestation.",
        ],
        bullets: [
          "Capture partielle : vous retenez 180 € sur une caution de 1 000 € pour une rayure",
          "Capture totale : en cas de dommage majeur ou de non-restitution",
          "Chaque capture est tracée avec sa réservation, son montant et sa justification",
        ],
      },
      {
        heading: "Libération automatique",
        paragraphs: [
          "Aucun dommage constaté ? La caution est libérée : le blocage disparaît de la carte du client sans aucune action de votre part. L'onglet Revenus → Cautions vous montre en permanence ce qui est bloqué, capturé ou libéré, réservation par réservation.",
        ],
      },
    ],
  },
  {
    slug: "multi-agences-lieux",
    category: "demarrage",
    title: "Multi-agences : créer vos agences et organiser votre flotte",
    excerpt:
      "Créez vos agences et lieux de retrait, affectez vos véhicules et votre équipe, et gardez une vision consolidée de tous vos sites.",
    readTime: "5 min",
    sections: [
      {
        heading: "Créer vos agences",
        paragraphs: [
          "Dans Paramétrages → Agences, créez chaque site avec son nom, son adresse et ses horaires. Les agences apparaissent comme lieux de départ et de retour dans vos réservations, vos devis et sur votre site internet de réservation.",
        ],
      },
      {
        heading: "Affecter véhicules et équipe",
        paragraphs: [
          "Rattachez chaque véhicule à son agence d'attache : le planning et les disponibilités en tiennent compte automatiquement. Côté équipe, un collaborateur rattaché à une agence ne voit que les réservations dont le départ a lieu sur son site — chaque équipe travaille sur son périmètre.",
        ],
      },
      {
        heading: "La vision consolidée reste à la direction",
        paragraphs: [
          "En tant que dirigeant, vous conservez la vue d'ensemble : calendrier global, revenus par agence, statistiques consolidées. Les filtres par agence sont disponibles dans toutes les vues du Pilotage pour analyser chaque site individuellement.",
          "Ouvrir un nouveau site ? Créez l'agence, affectez ses véhicules, invitez son équipe : votre organisation est prête en quelques minutes.",
        ],
      },
    ],
  },
  {
    slug: "journal-activite-securite",
    category: "equipe",
    title: "Journal d'activité et sécurité du compte",
    excerpt:
      "Qui a modifié ce tarif ? Qui a annulé cette réservation ? Le journal d'activité trace chaque action, et votre compte est protégé contre les intrusions.",
    readTime: "4 min",
    sections: [
      {
        heading: "Chaque action tracée",
        paragraphs: [
          "Le journal d'activité enregistre automatiquement chaque action sensible réalisée sur votre compte : création ou annulation de réservation, modification de tarif, changement de permissions, émission de facture… Pour chaque entrée : l'auteur, la date et l'adresse IP.",
          "Fini les mystères : « qui a changé ça ? » trouve sa réponse en quelques secondes, avec l'ancienne et la nouvelle valeur.",
        ],
      },
      {
        heading: "Protection contre les intrusions",
        paragraphs: [
          "La connexion est verrouillée automatiquement pendant 15 minutes après 5 tentatives échouées : les attaques par force brute sont bloquées avant d'aboutir. Les mots de passe sont chiffrés avec les standards de l'industrie et ne sont jamais lisibles, même par notre équipe.",
        ],
      },
      {
        heading: "Bonnes pratiques",
        bullets: [
          "Un compte par collaborateur : jamais de mot de passe partagé",
          "Attribuez le rôle minimum nécessaire à chaque membre de l'équipe",
          "Désactivez immédiatement le compte d'un collaborateur qui quitte l'entreprise",
          "Consultez le journal d'activité après tout comportement inhabituel",
        ],
        paragraphs: [],
      },
    ],
  },
  {
    slug: "sinistres-declarer-suivre",
    category: "flotte",
    title: "Sinistres : déclarer et suivre un incident",
    excerpt:
      "Accrochage, bris de glace, vandalisme : déclarez le sinistre en deux minutes, rattachez-le au véhicule et à la réservation, et suivez son règlement.",
    readTime: "5 min",
    sections: [
      {
        heading: "Déclarer un sinistre",
        paragraphs: [
          "Depuis le Pilotage, créez un sinistre en renseignant le véhicule, la date, le type d'incident et les circonstances. Si une réservation était en cours à cette date, Junago la retrouve et la rattache : le client et le contrat concernés sont immédiatement identifiés.",
          "Les informations de votre assurance (compagnie, numéro de contrat) sont reprises automatiquement depuis les documents du véhicule pour constituer le dossier.",
        ],
      },
      {
        heading: "Suivre le dossier jusqu'au règlement",
        paragraphs: [
          "Chaque sinistre avance par statuts : déclaré, en cours d'expertise, réparation, clos. Ajoutez photos, montants (franchise, remboursement, coût de réparation) et notes au fil de l'eau : tout l'historique reste attaché au véhicule.",
        ],
      },
      {
        heading: "L'impact sur votre activité",
        paragraphs: [
          "Un véhicule accidenté peut être passé en statut « immobilisé » pour le retirer des disponibilités le temps des réparations. Les coûts saisis alimentent la rentabilité par véhicule : vous mesurez l'impact réel de chaque sinistre sur votre marge.",
        ],
      },
    ],
  },
  {
    slug: "contraventions-designation-conducteur",
    category: "flotte",
    title: "Contraventions : enregistrer l'avis et désigner le conducteur",
    excerpt:
      "Une amende arrive pour un de vos véhicules ? Junago retrouve la réservation concernée, identifie le conducteur et vous aide à respecter le délai de désignation.",
    readTime: "5 min",
    sections: [
      {
        heading: "Enregistrer la contravention",
        paragraphs: [
          "Créez la contravention avec le véhicule, la date et l'heure de l'infraction, le montant et la date limite de désignation. Junago détecte automatiquement la réservation en cours au moment de l'infraction : le client conducteur est identifié sans fouiller votre planning.",
        ],
      },
      {
        heading: "Désigner le conducteur dans les délais",
        paragraphs: [
          "En tant que loueur, vous êtes tenu de désigner le conducteur auprès de l'administration dans le délai légal. La fiche contravention regroupe toutes les informations nécessaires à la désignation (identité, permis, adresse du client) et suit la date limite pour que rien ne vous échappe.",
        ],
      },
      {
        heading: "Suivre chaque amende jusqu'à la clôture",
        paragraphs: [
          "Chaque contravention avance par statuts : reçue, conducteur désigné, payée, contestée, clôturée. L'historique complet reste rattaché au véhicule et à la réservation — utile en cas de litige ou de récidive du même client.",
        ],
      },
    ],
  },
  {
    slug: "import-csv-vehicules-clients",
    category: "demarrage",
    title: "Import CSV : migrer vos véhicules et vos clients",
    excerpt:
      "Quittez Excel sans ressaisie : importez votre flotte et votre fichier client en quelques minutes grâce à l'import CSV.",
    readTime: "4 min",
    sections: [
      {
        heading: "Préparer votre fichier",
        paragraphs: [
          "Exportez vos données existantes (Excel, Google Sheets ou votre ancien logiciel) au format CSV. Pour les véhicules : marque, modèle, immatriculation, kilométrage, catégorie, tarif. Pour les clients : nom, prénom, email, téléphone, adresse. Pas besoin d'un fichier parfait : vous pourrez compléter les fiches ensuite.",
        ],
      },
      {
        heading: "Importer en trois étapes",
        bullets: [
          "1. Ouvrez l'import CSV depuis l'onglet Véhicules ou Clients du Pilotage",
          "2. Faites correspondre vos colonnes avec les champs Junago (l'outil propose une correspondance automatique)",
          "3. Vérifiez l'aperçu et validez : vos données sont créées en quelques secondes",
        ],
        paragraphs: [],
      },
      {
        heading: "Après l'import",
        paragraphs: [
          "Complétez progressivement vos fiches : photos et documents des véhicules (assurance, contrôle technique), permis des clients. La plupart des loueurs migrent l'essentiel de leur activité en moins d'une heure — et abandonnent définitivement leur tableur dans les deux premières semaines.",
        ],
      },
    ],
  },
  {
    slug: "etats-des-lieux-comparaison",
    category: "legal",
    title: "États des lieux : comparer départ et retour, gérer les aggravations",
    excerpt:
      "Photos horodatées au départ, comparaison automatique au retour, aggravations chiffrées face au client : des restitutions sans litige.",
    readTime: "6 min",
    sections: [
      {
        heading: "L'état des lieux de départ",
        paragraphs: [
          "Avant chaque remise de clés, réalisez l'état des lieux directement depuis la réservation : photos de la carrosserie sous tous les angles, kilométrage, niveau de carburant, dommages existants annotés. Chaque photo est horodatée — la preuve est constituée face au client, en quelques minutes.",
        ],
      },
      {
        heading: "La comparaison automatique au retour",
        paragraphs: [
          "Au retour du véhicule, l'état des lieux de restitution se compare automatiquement à celui du départ : Junago met en regard les deux relevés, zone par zone. Les différences — nouvelle rayure, jante marquée, carburant manquant — apparaissent immédiatement.",
        ],
      },
      {
        heading: "Gérer une aggravation",
        paragraphs: [
          "Une aggravation constatée se documente sur place : photo du dommage, description, montant estimé de la remise en état. Le client signe le relevé en toute transparence. Si une caution par empreinte bancaire est en place, vous pouvez capturer le montant exact du dommage, preuves à l'appui.",
        ],
        bullets: [
          "Les deux relevés signés restent attachés à la réservation, consultables à tout moment",
          "En cas de litige ou de dossier d'assurance, l'historique photo horodaté fait la différence",
          "Aucun dommage ? La comparaison le confirme et la caution est libérée",
        ],
      },
    ],
  },

];

export const HELP_FAQ = [
  {
    category: "Compte & abonnement",
    items: [
      {
        q: "Comment créer mon compte loueur ?",
        a: "Rendez-vous sur la page « Devenir loueur » puis cliquez sur « Commencer gratuitement ». Renseignez les informations de votre entreprise, ajoutez vos premiers véhicules et vous êtes opérationnel en quelques minutes.",
      },
      {
        q: "L'essai gratuit de 30 jours inclut-il toutes les fonctionnalités ?",
        a: "Oui. Pendant 30 jours, vous accédez à l'intégralité du logiciel sans aucune limite : flotte illimitée, contrats, signature électronique, encaissements par carte, CRM. Aucune carte bancaire n'est requise.",
      },
      {
        q: "Puis-je résilier à tout moment ?",
        a: "Oui, la résiliation se fait en 1 clic depuis vos paramétrages, sans engagement ni préavis. Vos données restent exportables.",
      },
    ],
  },
  {
    category: "Réservations & encaissements",
    items: [
      {
        q: "Comment encaisser mes clients ?",
        a: "Activez Stripe depuis votre back office : vos clients paient par carte bancaire et les fonds sont reversés directement sur votre compte. Aucune donnée bancaire n'est stockée chez Junago.",
      },
      {
        q: "Puis-je créer une réservation manuellement ?",
        a: "Oui. Le bouton « + Créer » du back office permet d'enregistrer une réservation prise par téléphone ou à l'agence. Elle bénéficie du suivi complet : contrat, état des lieux, tâches automatiques.",
      },
      {
        q: "Comment fonctionnent les devis ?",
        a: "Créez un devis chiffré depuis le Pilotage (véhicule, dates, options), envoyez-le à votre prospect. Un devis accepté se transforme en réservation confirmée en un clic.",
      },
    ],
  },
  {
    category: "Flotte & véhicules",
    items: [
      {
        q: "Combien de véhicules puis-je gérer ?",
        a: "Autant que vous le souhaitez. Junago est conçu pour gérer des flottes de toutes tailles, du loueur indépendant à l'agence multi-sites.",
      },
      {
        q: "Comment suis-je alerté de l'expiration de mes documents ?",
        a: "Ajoutez une date d'expiration sur chaque document véhicule (assurance, contrôle technique). Junago vous alerte automatiquement avant l'échéance, selon le délai configuré dans vos paramétrages.",
      },
      {
        q: "Puis-je créer mes propres catégories et statuts ?",
        a: "Oui, dans Paramétrages > Flotte : catégories réorganisables par glisser-déposer et statuts personnalisés avec code couleur (disponible, en entretien, immobilisé…).",
      },
    ],
  },
  {
    category: "Contrats & légal",
    items: [
      {
        q: "Les contrats sont-ils générés automatiquement ?",
        a: "Oui. Pour chaque réservation, Junago génère un contrat pré-rempli avec les informations du locataire, du véhicule et de la location. Le locataire le signe électroniquement, sur place ou à distance.",
      },
      {
        q: "La signature électronique a-t-elle une valeur légale ?",
        a: "La signature est horodatée et attachée au contrat, avec un exemplaire remis à chaque partie. Elle constitue un élément de preuve en cas de litige.",
      },
      {
        q: "Comment fonctionnent les états des lieux ?",
        a: "Au départ et au retour, réalisez un état des lieux photo directement depuis la plateforme : carrosserie, kilométrage, carburant. Les deux relevés sont comparés automatiquement.",
      },
    ],
  },
  {
    category: "Site internet de réservation",
    items: [
      {
        q: "Comment mes clients réservent-ils en ligne ?",
        a: "Votre site internet de réservation affiche vos véhicules et vos disponibilités réelles. Le client choisit ses dates, son véhicule et ses options, paie en ligne (acompte ou intégral selon vos réglages) : la réservation confirmée apparaît instantanément dans votre planning.",
      },
      {
        q: "Puis-je personnaliser mon site de réservation ?",
        a: "Oui : logo, couleurs, textes d'accueil, coordonnées, horaires et photos sont modifiables depuis Paramétrages → Site de réservation. Un lien de prévisualisation privé vous permet de vérifier le rendu avant publication.",
      },
      {
        q: "Puis-je choisir quels véhicules sont visibles en ligne ?",
        a: "Oui, vous contrôlez véhicule par véhicule ce qui est publié, ainsi que les tarifs, durées minimum et conditions affichées. Un véhicule dépublié reste utilisable pour vos réservations au comptoir.",
      },
      {
        q: "Le site est-il hébergé par Junago ?",
        a: "Oui : hébergement, certificat SSL et optimisation SEO sont inclus. Il n'y a rien à installer ni à maintenir — chaque modification dans votre logiciel est répercutée automatiquement sur le site.",
      },
    ],
  },
  {
    category: "Produits & options",
    items: [
      {
        q: "Comment vendre des sièges bébé, assurances ou autres extras ?",
        a: "Créez vos produits et options dans Paramétrages → Produits & options (libellé, prix par jour ou par réservation, TVA). Ils sont proposés automatiquement à l'étape des extras sur votre site de réservation et dans vos devis et réservations au comptoir.",
      },
      {
        q: "Comment gérer le stock d'options par agence ?",
        a: "Définissez une quantité par agence (5 sièges bébé à Bordeaux, 3 à Toulouse) : chaque site vend ce qu'il possède réellement. Sans quantité précisée, le stock global s'applique automatiquement. Trois modes existent : strict (vente bloquée), souple (alerte) ou illimité (services).",
      },
      {
        q: "Puis-je suivre la rentabilité de mes options ?",
        a: "Oui : l'onglet Revenus consolide le chiffre d'affaires et le volume de chaque produit. Vous savez immédiatement quelles options performent — le rachat de franchise et la livraison sont souvent les plus rentables.",
      },
    ],
  },
  {
    category: "Facturation & cautions",
    items: [
      {
        q: "Les factures générées sont-elles conformes ?",
        a: "Oui : numérotation légale continue, mentions obligatoires et TVA calculée automatiquement. Générez une facture depuis n'importe quelle réservation ou créez une facture libre pour toute autre prestation.",
      },
      {
        q: "Comment faire un avoir ?",
        a: "Depuis la facture concernée, créez un avoir en un clic : il est automatiquement lié à la facture d'origine et intégré à votre numérotation. Idéal pour un remboursement partiel ou une correction.",
      },
      {
        q: "Comment fonctionne l'empreinte bancaire pour la caution ?",
        a: "Au départ du véhicule, le montant de la caution est bloqué sur la carte du client sans être débité. Au retour, la caution est libérée automatiquement s'il n'y a aucun dommage — le client ne voit jamais son compte débité.",
      },
      {
        q: "Puis-je capturer une partie seulement de la caution ?",
        a: "Oui : en cas de dommage constaté à l'état des lieux retour, capturez le montant exact de la réparation (par exemple 180 € sur 1 000 € bloqués). Les photos horodatées de l'état des lieux servent de preuve, et le reste de la caution est libéré.",
      },
    ],
  },
  {
    category: "Équipe & permissions",
    items: [
      {
        q: "Puis-je limiter les droits d'un collaborateur ?",
        a: "Oui : chaque collaborateur a son propre compte et un rôle précis (Manager, Agent, Comptable ou rôle sur mesure). Les droits se règlent finement par module et par action : un agent de comptoir peut créer des réservations et encaisser sans jamais voir vos revenus.",
      },
      {
        q: "Un employé peut-il ne voir qu'une seule agence ?",
        a: "Oui : rattachez le collaborateur à son agence, il ne verra que les réservations dont le départ a lieu sur son site — dans l'interface comme dans les exports. Vous conservez la vision consolidée de tous vos sites.",
      },
      {
        q: "Comment savoir qui a modifié quoi ?",
        a: "Le journal d'activité enregistre chaque action sensible avec son auteur, la date et l'adresse IP : modification de tarif, annulation, changement de permissions… « Qui a fait ça ? » trouve sa réponse en quelques secondes.",
      },
    ],
  },
  {
    category: "Junago Copilot & IA",
    items: [
      {
        q: "Quelles données l'IA voit-elle ?",
        a: "Junago Copilot accède uniquement aux données de votre compte : flotte, réservations, clients, devis et revenus. Il ne voit jamais les données des autres loueurs — les mêmes règles d'isolation s'appliquent qu'au reste de la plateforme.",
      },
      {
        q: "Mes données servent-elles à entraîner des modèles d'IA ?",
        a: "Non. Vos données restent les vôtres et ne servent qu'à répondre à vos propres questions. Elles ne sont jamais utilisées pour entraîner des modèles, ni partagées avec des tiers.",
      },
      {
        q: "Copilot peut-il agir sans mon accord ?",
        a: "Non : pour toute action (créer une réservation, bloquer un véhicule…), Copilot prépare et vous propose — c'est toujours vous qui validez d'un clic. Les automatisations de fond (emails, tâches) suivent les règles que vous avez configurées.",
      },
    ],
  },
  {
    category: "Sécurité & données",
    items: [
      {
        q: "Mes données sont-elles isolées de celles des autres loueurs ?",
        a: "Oui : chaque compte est strictement cloisonné. Vos véhicules, clients, réservations et finances ne sont accessibles qu'à votre équipe, selon les permissions que vous avez définies.",
      },
      {
        q: "Comment mon compte est-il protégé contre les intrusions ?",
        a: "La connexion est verrouillée automatiquement pendant 15 minutes après 5 tentatives échouées, les mots de passe sont chiffrés avec les standards de l'industrie et chaque action sensible est tracée dans le journal d'activité.",
      },
      {
        q: "Puis-je récupérer mes données si je pars ?",
        a: "Oui : vos données vous appartiennent. Réservations, clients, véhicules et écritures comptables sont exportables en CSV à tout moment, y compris après résiliation de votre abonnement.",
      },
    ],
  },
];

// ─── FAQ courte pour la page /devenir-loueur ───
export const BECOME_HOST_FAQ = [
  {
    q: "Que fait Junago Copilot exactement ?",
    a: "Junago Copilot est l'assistant IA intégré au logiciel. Vous lui parlez en langage naturel (« Montre-moi mon planning », « Qui est mon meilleur client ? ») et il répond avec vos vraies données, sous forme de vignettes interactives. Il permet aussi de créer une réservation ou de gérer vos tâches recommandées sans naviguer dans les onglets. Junago pilote, Junago Copilot assiste.",
  },
  {
    q: "L'IA peut-elle agir sans mon accord ?",
    a: "Non. Aujourd'hui, Junago Copilot affiche, suggère et prépare — mais chaque action (accepter une réservation, marquer une tâche terminée…) reste déclenchée par vous, d'un clic. L'agent IA autonome prévu à la roadmap fonctionnera lui aussi avec votre validation.",
  },
  {
    q: "L'essai gratuit est-il vraiment sans engagement ?",
    a: "Oui. 30 jours d'accès complet à toutes les fonctionnalités, sans carte bancaire et résiliable en 1 clic. Vous décidez ensuite librement de continuer.",
  },
  {
    q: "En combien de temps puis-je démarrer ?",
    a: "Quelques minutes suffisent : créez votre compte, renseignez les informations de votre entreprise (elles alimentent automatiquement vos contrats et devis) et ajoutez vos premiers véhicules.",
  },
  {
    q: "Puis-je encaisser mes clients par carte bancaire ?",
    a: "Oui. Activez Stripe depuis votre back office : vos clients paient par carte et les fonds sont reversés directement sur votre compte bancaire. Aucune donnée bancaire n'est stockée chez Junago.",
  },
  {
    q: "Puis-je gérer mes réservations téléphoniques et à l'agence ?",
    a: "Oui. Le back office permet de créer des réservations en quelques secondes. Chacune bénéficie du suivi complet : contrat, signature, état des lieux, tâches automatiques.",
  },
  {
    q: "Les contrats et états des lieux sont-ils inclus ?",
    a: "Oui, dans toutes les offres : génération automatique des contrats, signature électronique horodatée et états des lieux photo au départ comme au retour.",
  },
  {
    q: "Combien de véhicules puis-je gérer ?",
    a: "Autant que nécessaire. Junago s'adapte du loueur indépendant avec 2 véhicules à l'agence multi-sites avec une flotte complète.",
  },
  {
    q: "Mes données sont-elles en sécurité ?",
    a: "Vos données sont hébergées en Europe et vos transactions chiffrées par Stripe (certifié PCI-DSS niveau 1). Vous restez propriétaire de vos données clients.",
  },
  {
    q: "Le site internet de réservation est-il inclus ?",
    a: "Oui. Junago génère et héberge votre site internet de réservation professionnel : disponibilités en temps réel, paiement en ligne, SSL et SEO inclus. Tout ce que vous modifiez dans le logiciel (véhicules, tarifs, options) est répercuté automatiquement sur le site, sans double saisie.",
  },
  {
    q: "Puis-je vendre des produits et options en plus de la location ?",
    a: "Oui. Créez vos produits et options (siège bébé, rachat de franchise, livraison, nettoyage…) : ils sont proposés automatiquement au bon moment du parcours de réservation, en ligne comme au comptoir, et intégrés au contrat et à la facture.",
  },
  {
    q: "Comment sont suivis mes revenus et ma facturation ?",
    a: "Le cockpit Revenus centralise encaissements, factures, avoirs, dépenses et cautions. Les factures respectent une numérotation légale continue avec TVA automatique, et l'export comptable CSV se génère par période pour votre expert-comptable.",
  },
  {
    q: "Puis-je inviter mon équipe avec des droits limités ?",
    a: "Oui. Chaque collaborateur a son propre compte et un rôle précis (Manager, Agent, Comptable ou sur mesure). Un agent de comptoir peut créer des réservations et encaisser sans jamais voir vos marges, et chaque action est tracée dans le journal d'activité.",
  },
];
